/**
 * Opening Hours Settings Component
 * Verwaltet die Öffnungszeiten der Werkstatt
 */

import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  TextField,
  Button,
  Alert,
  Grid,
  FormControlLabel,
  Switch,
  Divider,
  Chip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  AccessTime as AccessTimeIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  HelpOutline as HelpOutlineIcon,
} from "@mui/icons-material";
import { apiClient } from "../../services/apiClient";
import {
  OpeningHours,
  DayOfWeek,
  BusinessStatusMode,
} from "../../types/api-types";
import LoadingSpinner from "../../components/Common/LoadingSpinner";
import ThreeCXIntegration from "./ThreeCXIntegration";

interface OpeningHoursFormData {
  [key: string]: {
    openTime: string;
    closeTime: string;
    isClosed: boolean;
  };
}

const OpeningHoursSettings: React.FC = () => {
  const [openingHours, setOpeningHours] = useState<OpeningHours[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingDay, setSavingDay] = useState<string | null>(null);
  const [editingDay, setEditingDay] = useState<string | null>(null);

  const [formData, setFormData] = useState<OpeningHoursFormData>({});

  // Standard-Status innerhalb der Öffnungszeiten (Auf Anfrage / Geöffnet)
  const [defaultOpenStatus, setDefaultOpenStatus] = useState<BusinessStatusMode>(
    BusinessStatusMode.REQUEST
  );
  const [savingDefaultStatus, setSavingDefaultStatus] = useState(false);

  const loadDefaultOpenStatus = async () => {
    try {
      const { status } = await apiClient.getDefaultOpenStatus();
      setDefaultOpenStatus(status);
    } catch (err: any) {
      setError(err.message || "Fehler beim Laden des Standard-Status");
    }
  };

  const handleDefaultStatusChange = async (status: BusinessStatusMode) => {
    if (!status || status === defaultOpenStatus) return;
    const previous = defaultOpenStatus;
    try {
      setSavingDefaultStatus(true);
      setError(null);
      setDefaultOpenStatus(status); // optimistisch
      const result = await apiClient.setDefaultOpenStatus(status);
      setDefaultOpenStatus(result.status);
    } catch (err: any) {
      setDefaultOpenStatus(previous); // zurückrollen
      setError(err.message || "Fehler beim Speichern des Standard-Status");
    } finally {
      setSavingDefaultStatus(false);
    }
  };

  // Load opening hours
  const loadOpeningHours = async () => {
    try {
      setLoading(true);
      setError(null);
      const hours = await apiClient.getOpeningHours();
      setOpeningHours(hours);

      // Initialize form data
      const initialFormData: OpeningHoursFormData = {};
      hours.forEach((hour) => {
        initialFormData[hour.dayOfWeek] = {
          openTime: hour.openTime || "",
          closeTime: hour.closeTime || "",
          isClosed: hour.isClosed,
        };
      });
      setFormData(initialFormData);
    } catch (err: any) {
      setError(err.message || "Fehler beim Laden der Öffnungszeiten");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOpeningHours();
    loadDefaultOpenStatus();
  }, []);

  const handleInputChange = (dayOfWeek: string, field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [dayOfWeek]: {
        ...prev[dayOfWeek],
        [field]: value,
      },
    }));
  };

  const handleSaveDay = async (dayOfWeek: DayOfWeek) => {
    try {
      setSavingDay(dayOfWeek);
      setError(null);

      const data = formData[dayOfWeek];

      await apiClient.setOpeningHoursWith3CX({
        dayOfWeek,
        openTime: data.isClosed ? undefined : data.openTime || undefined,
        closeTime: data.isClosed ? undefined : data.closeTime || undefined,
        isClosed: data.isClosed,
      });

      await loadOpeningHours();
      setEditingDay(null);
    } catch (err: any) {
      setError(err.message || "Fehler beim Speichern des Wochentags");
    } finally {
      setSavingDay(null);
    }
  };

  const handleCancelDay = (dayOfWeek: DayOfWeek) => {
    const originalData = openingHours.find((hour) => hour.dayOfWeek === dayOfWeek);
    setFormData((prev) => ({
      ...prev,
      [dayOfWeek]: {
        openTime: originalData?.openTime || "",
        closeTime: originalData?.closeTime || "",
        isClosed: originalData?.isClosed ?? true,
      },
    }));
    setEditingDay(null);
  };

  const getDayName = (dayOfWeek: DayOfWeek): string => {
    const dayNames: Record<DayOfWeek, string> = {
      [DayOfWeek.MONDAY]: "Montag",
      [DayOfWeek.TUESDAY]: "Dienstag",
      [DayOfWeek.WEDNESDAY]: "Mittwoch",
      [DayOfWeek.THURSDAY]: "Donnerstag",
      [DayOfWeek.FRIDAY]: "Freitag",
      [DayOfWeek.SATURDAY]: "Samstag",
      [DayOfWeek.SUNDAY]: "Sonntag",
    };
    return dayNames[dayOfWeek];
  };

  const formatTime = (time: string | undefined): string => {
    if (!time) return "Nicht angegeben";
    return time;
  };

  if (loading) {
    return <LoadingSpinner text="Lade Öffnungszeiten..." />;
  }

  return (
    <>
      {/* Standard-Status innerhalb der Öffnungszeiten */}
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          mb: 3,
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            color="primary"
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <CheckCircleIcon />
            Standard-Status während der Öffnungszeiten
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            Welcher Status soll automatisch gelten, solange innerhalb der
            Öffnungszeiten kein manueller Status gesetzt ist? Dieser Status wird
            öffentlich (z. B. auf der Website) angezeigt.
          </Typography>

          <ToggleButtonGroup
            exclusive
            value={defaultOpenStatus}
            onChange={(_e, value) => handleDefaultStatusChange(value)}
            disabled={savingDefaultStatus}
            sx={{
              flexWrap: "wrap",
              gap: 1,
              "& .MuiToggleButton-root": {
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                textTransform: "none",
                px: 2,
                py: 1,
              },
            }}
          >
            <ToggleButton
              value={BusinessStatusMode.REQUEST}
              sx={{
                "&.Mui-selected": {
                  bgcolor: "warning.main",
                  color: "white",
                  "&:hover": { bgcolor: "warning.dark" },
                },
              }}
            >
              <HelpOutlineIcon sx={{ mr: 1 }} fontSize="small" />
              Auf Anfrage
            </ToggleButton>
            <ToggleButton
              value={BusinessStatusMode.OPEN}
              sx={{
                "&.Mui-selected": {
                  bgcolor: "success.main",
                  color: "white",
                  "&:hover": { bgcolor: "success.dark" },
                },
              }}
            >
              <CheckCircleIcon sx={{ mr: 1 }} fontSize="small" />
              Geöffnet
            </ToggleButton>
          </ToggleButtonGroup>

          {savingDefaultStatus && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Wird gespeichert...
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 2, md: 0 },
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              color="primary"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <AccessTimeIcon />
              Öffnungszeiten
            </Typography>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {/* 3CX Status Chip */}


              <Chip
                size="small"
                icon={<LockIcon />}
                label="Tage einzeln bearbeitbar"
                variant="outlined"
              />
            </Box>
          </Box>

          {/* 3CX Integration */}
          <ThreeCXIntegration
            title="3CX Öffnungszeiten Integration"
            type="opening-hours"
            onSyncSuccess={() => {
              setError(null);
              loadOpeningHours(); // Reload opening hours after sync
            }}
            onSyncError={(error) => {
              setError(`3CX Synchronisation fehlgeschlagen: ${error}`);
            }}
          />

          <Divider sx={{ mb: 3 }} />

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3, borderRadius: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* Opening Hours Display/Edit */}
          <Stack spacing={2}>
            {Object.values(DayOfWeek).map((dayOfWeek) => {
              const dayData = formData[dayOfWeek];
              const originalData = openingHours.find(
                (h) => h.dayOfWeek === dayOfWeek
              );

              return (
                <Card
                  key={dayOfWeek}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    bgcolor: dayData?.isClosed ? "grey.50" : "transparent",
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="medium"
                      gutterBottom
                      sx={{
                        textDecoration: dayData?.isClosed
                          ? "line-through"
                          : "none",
                        color: dayData?.isClosed
                          ? "text.secondary"
                          : "text.primary",
                      }}
                    >
                      {getDayName(dayOfWeek)}
                    </Typography>

                    {editingDay === dayOfWeek ? (
                      <Stack spacing={2}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!dayData?.isClosed}
                              onChange={(e) =>
                                handleInputChange(
                                  dayOfWeek,
                                  "isClosed",
                                  !e.target.checked
                                )
                              }
                              color="primary"
                              size="small"
                            />
                          }
                          label="Geöffnet"
                        />

                        {!dayData?.isClosed && (
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <TextField
                                fullWidth
                                label="Öffnung"
                                type="time"
                                value={dayData?.openTime || ""}
                                onChange={(e) =>
                                  handleInputChange(
                                    dayOfWeek,
                                    "openTime",
                                    e.target.value
                                  )
                                }
                                size="small"
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                fullWidth
                                label="Schließung"
                                type="time"
                                value={dayData?.closeTime || ""}
                                onChange={(e) =>
                                  handleInputChange(
                                    dayOfWeek,
                                    "closeTime",
                                    e.target.value
                                  )
                                }
                                size="small"
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                          </Grid>
                        )}

                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                          justifyContent="flex-end"
                          alignItems={{ xs: "stretch", sm: "center" }}
                        >
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                              variant="outlined"
                              startIcon={<CancelIcon />}
                              onClick={() => handleCancelDay(dayOfWeek)}
                              disabled={savingDay === dayOfWeek}
                              size="small"
                            >
                              Abbrechen
                            </Button>
                            <Button
                              variant="contained"
                              startIcon={
                                savingDay === dayOfWeek ? (
                                  <CircularProgress size={18} color="inherit" />
                                ) : (
                                  <SaveIcon />
                                )
                              }
                              onClick={() => handleSaveDay(dayOfWeek)}
                              disabled={savingDay === dayOfWeek}
                              size="small"
                              sx={{ minWidth: 112 }}
                            >
                              {savingDay === dayOfWeek ? "Speichert..." : "Speichern"}
                            </Button>
                          </Box>
                        </Stack>
                      </Stack>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: { xs: "flex-start", sm: "center" },
                          gap: 2,
                          flexDirection: { xs: "column", sm: "row" },
                        }}
                      >
                        <Box>
                          {originalData?.isClosed ? (
                            <Chip
                              label="Geschlossen"
                              color="default"
                              variant="outlined"
                              size="small"
                            />
                          ) : (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip
                                label="Geöffnet"
                                color="success"
                                variant="outlined"
                                size="small"
                              />
                              <Typography variant="body2">
                                {formatTime(originalData?.openTime)} -{" "}
                                {formatTime(originalData?.closeTime)}
                              </Typography>
                            </Stack>
                          )}
                        </Box>
                        <Button
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => setEditingDay(dayOfWeek)}
                          disabled={Boolean(editingDay)}
                          size="small"
                        >
                          Bearbeiten
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </CardContent>
      </Card>
    </>
  );
};

export default OpeningHoursSettings;
